import { InformationCircleIcon, WrenchIcon } from "@heroicons/react/24/outline";
import FileInputButton from "@via/components/inputs/file-input-button.tsx";
import ValueInputButton from "@via/components/inputs/value-input-button.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component";
import FormTextareaComponent from "@via/components/inputs/form-textarea-component";
import Modal from "@via/components/modal.tsx";
import { HeadingSize, PageHeading } from "@via/components/page-heading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import HorizontalInputsArray from "@via/pages/projects/runs/overview/inputs/horizontal-inputs-array/horizontal-inputs-array.tsx";
import IntegrationTokenSelector from "@via/pages/projects/runs/overview/inputs/integration-token-selector.tsx";
import InputFileModal from "@via/pages/projects/runs/overview/inputs/input-file/input-file-modal.tsx";
import InputSingleFileModal from "@via/pages/projects/runs/overview/inputs/input-file/input-single-file-modal.tsx";
import InputSpreadsheet from "@via/pages/projects/runs/overview/inputs/input-spreadsheet/input-spreadsheet.tsx";
import InputValueModal from "@via/pages/projects/runs/overview/inputs/input-value/input-value-modal.tsx";
import { isVmetaCollection } from "@via/pages/projects/runs/overview/inputs/input-file/single-file-spreadsheet";
import { getVmetaCollectionLink } from "@via/pages/projects/runs/overview/utils/vmeta-dataset-menu-items";
import {
  type InputNode,
  type ProcessAndModuleOptions,
  groupProcessInputs,
  parseDropdownOptions,
  shouldDisplayInput,
} from "@via/pages/projects/runs/utils/helpers.tsx";
import { ModalSize } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { createMarkup } from "@via/utils/html-utils.tsx";
import { type CollectionItem } from "@via/utils/interfaces.tsx";
import { InputType, SaveRunInputInput, type SaveRunInputs } from "common";
import {
  Dispatch,
  type FC,
  SetStateAction,
  useCallback,
  useState,
} from "react";
import { type UseFormReturn } from "react-hook-form";
import { Tooltip } from "react-tooltip";

interface ShowSettingsModalProps {
  showSettings: string[];
  setShowSettings: (showSettings: string[]) => void;
  form: UseFormReturn;
  allGlobalInputs: InputNode[];
  allPipelineInputs: InputNode[];
  allProcessAndModuleInputs: ProcessAndModuleOptions[];
  processOptions?: Record<string, any>;
  runUUID?: string | null;
  mainScriptHeaderConfig: string;
  paramsInputs: SaveRunInputs[];
  setParamsInputs: Dispatch<SetStateAction<SaveRunInputs[]>>;
  readOnly: boolean;
  editorProcesses: Record<string, any>;
  runOwnerId?: number;
}

const ShowSettingsModal: FC<ShowSettingsModalProps> = ({
  showSettings,
  setShowSettings,
  form,
  allGlobalInputs,
  allPipelineInputs,
  allProcessAndModuleInputs,
  processOptions,
  runUUID,
  mainScriptHeaderConfig,
  paramsInputs,
  setParamsInputs,
  readOnly,
  editorProcesses,
  runOwnerId,
}) => {
  const { control, setValue, watch } = form;

  const [openInputFileModal, setOpenInputFileModal] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [openInputSingleFileModal, setOpenInputSingleFileModal] =
    useState<Record<string, unknown> | null>(null);
  const [singleFileEditor, setSingleFileEditor] = useState<
    Record<string, any> | undefined
  >();
  const [openInputValueModal, setOpenInputValueModal] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [openShowSettingsModal, setOpenShowSettingsModal] = useState<string[]>(
    []
  );

  const syncIntegrationTokenToParamsInputs = useCallback(
    (inputName: string, tokenId: string | null) => {
      setParamsInputs(prev => {
        const idx = prev.findIndex(p => p.name === inputName);
        const existing = idx >= 0 ? prev[idx] : null;
        const serviceTokenId = tokenId ? Number(tokenId) : undefined;
        const newEntry: SaveRunInputs = {
          name: inputName,
          type: InputType.Input,
          value: serviceTokenId != null ? String(serviceTokenId) : tokenId,
          ...(serviceTokenId != null && { serviceTokenId }),
          ...(existing &&
            "id" in existing &&
            existing.id != null && { id: existing.id }),
        };
        if (idx >= 0) {
          return prev.slice(0, idx).concat([newEntry], prev.slice(idx + 1));
        }
        if (tokenId) return prev.concat([newEntry]);
        return prev;
      });
    },
    [setParamsInputs]
  );

  const handleSaveInputFiles = (
    inputId: string,
    collection: CollectionItem
  ): void => {
    if (inputId.startsWith("params.")) {
      const name = inputId.replace("params.", "");

      setParamsInputs((prev: SaveRunInputs[]) => {
        const idx = prev.findIndex(p => p.name === name);

        // Build the correct union member
        const next: SaveRunInputs = {
          name,
          value: collection.value ?? collection.collectionName,
          ...(collection.collectionType === InputType.VmetaCollection
            ? {
                vmetaCollectionId: collection.collectionId || null,
                type: InputType.VmetaCollection,
              }
            : {
                collectionId: Number(collection.collectionId) || null,
                type: InputType.Collection,
              }),
        };

        // Preserve `id` if updating an existing item
        if (idx !== -1) {
          const keepId = prev[idx]?.id;
          const replaced = keepId != null ? { ...next, id: keepId } : next;
          return prev.map((p, i) => (i === idx ? replaced : p));
        }

        // Append new
        return [...prev, next];
      });
    }

    setValue(inputId, collection.collectionName);
    setOpenInputFileModal(null);
  };

  const handleSaveSingleInputFile = (
    inputId: string,
    value: string | null
  ): void => {
    if (inputId.startsWith("params.")) {
      setParamsInputs((prevParamsInputs: SaveRunInputs[]) => {
        const itemName = inputId.replace("params.", "");
        const existingItem = prevParamsInputs.some(
          ({ name }) => name === itemName
        );

        if (existingItem) {
          return prevParamsInputs.map(item =>
            item.name === itemName
              ? ({
                  ...item,
                  value,
                  type: InputType.Input,
                } as SaveRunInputInput)
              : item
          );
        } else {
          return [
            ...prevParamsInputs,
            {
              name: itemName,
              value,
              collectionId: null,
              type: InputType.Input,
            } as SaveRunInputInput,
          ];
        }
      });
    }
    setValue(inputId, value);
    // Also the multiple file dialog's onSaveValue, which still closes from here. The
    // single file dialog is closed by Modal once its confirm reports success.
    setOpenInputFileModal(null);
  };

  const handleSaveInputValue = (
    inputId: string,
    value: string | null
  ): void => {
    setValue(inputId, value);
  };

  const handleDeleteInputFiles = (inputId: string): void => {
    if (inputId.startsWith("params.")) {
      setParamsInputs((prevParamsInputs: SaveRunInputs[]) =>
        prevParamsInputs.map(item =>
          item.name === inputId.replace("params.", "")
            ? {
                ...item,
                value: null,
                collectionId: null,
                vmetaCollectionId: null,
              }
            : item
        )
      );
    }
    setValue(inputId, null);
  };

  const handleDeleteSingleInputFile = (inputId: string): void => {
    setValue(inputId, null);
  };

  const renderInputOptions = (
    input: InputNode,
    index: number,
    style?: Record<string, any>[]
  ): JSX.Element => {
    const value = watch(input.id);
    return (
      <div
        key={index}
        className={classNames(
          "mb-5",
          shouldDisplayInput(
            input.id,
            input.inputId,
            style?.[0]?.condi || [],
            mainScriptHeaderConfig,
            form
          )
            ? ""
            : "hidden"
        )}>
        {!input.inputDetails?.checkbox && (
          <label className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900">
            {input.inputDetails?.inLabelOpt ?? input.name}{" "}
            {input.inputDetails?.optional && "(Optional)"}
            {input.inputDetails?.tool && (
              <InformationCircleIcon
                data-tooltip-id={`info-${input.id}`}
                data-tooltip-content={input.inputDetails?.tool}
                className="mx-2 h-4"
              />
            )}
          </label>
        )}
        <div className="mt-2">
          {input.inputDetails?.integrationToken ? (
            <IntegrationTokenSelector
              control={control}
              name={input.id}
              tokenTypes={input.inputDetails.integrationToken.type}
              readOnly={readOnly}
              runOwnerId={runOwnerId}
              onSelectionChange={tokenId =>
                syncIntegrationTokenToParamsInputs(input.name, tokenId)
              }
            />
          ) : input.inputDetails?.dropDown ? (
            <div className="flex w-full">
              <div className="w-full">
                <SelectComponent
                  control={control}
                  name={input.id}
                  enableClear={false}
                  options={parseDropdownOptions(
                    input.id,
                    input.inputDetails?.dropDown || "",
                    mainScriptHeaderConfig,
                    form
                  )}
                  readOnly={readOnly}
                />
              </div>
              {input.inputDetails?.showSett && value === "yes" && (
                <button
                  className="ml-3 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => {
                    setOpenShowSettingsModal(
                      input.inputDetails?.showSett?.split(",") ?? []
                    );
                  }}>
                  <WrenchIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>
          ) : input.inputDetails?.checkbox ? (
            <label className="align-items flex flex-row items-center">
              <input
                type="checkbox"
                className="mr-2 block h-6 w-6 rounded-md border-0 text-indigo-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                checked={value === true || value === "true"}
                onChange={e => setValue(input.id, e.target.checked)}
                disabled={readOnly}
              />
              <div className="block text-sm font-medium leading-6 text-gray-900">
                {input.inputDetails?.inLabelOpt ?? input.name}
              </div>
              {input.inputDetails?.tool && (
                <InformationCircleIcon
                  data-tooltip-id={`info-${input.id}`}
                  data-tooltip-content={input.inputDetails?.tool}
                  className="mx-2 h-4"
                />
              )}
            </label>
          ) : input.inputDetails?.textbox ? (
            <FormTextareaComponent
              control={control}
              name={input.id}
              disabled={readOnly}
            />
          ) : input.qualifier === "val" ? (
            input.id.startsWith("params.") ? (
              <ValueInputButton
                control={control}
                name={input.id}
                onOpenModal={value =>
                  setOpenInputValueModal({ [input.id]: value })
                }
                onDeleteItem={() => setValue(input.id, null)}
                disabled={readOnly}
              />
            ) : (
              <FormInputComponent
                control={control}
                name={input.id}
                disabled={readOnly}
              />
            )
          ) : (
            <FileInputButton
              control={control}
              name={input.id}
              onOpenModal={value => {
                if (input.qualifier === "singleFile") {
                  setOpenInputSingleFileModal({ [input.id]: value });
                  setSingleFileEditor(input?.inputDetails?.editor);
                } else {
                  setOpenInputFileModal({ [input.id]: value });
                }
              }}
              onDeleteFile={() =>
                input.qualifier === "singleFile"
                  ? handleDeleteSingleInputFile(input.id)
                  : handleDeleteInputFiles(input.id)
              }
              disabled={readOnly}
              isCollection={
                input.qualifier !== "singleFile" &&
                paramsInputs.some(
                  param =>
                    param.name === input.id.replace("params.", "") &&
                    isVmetaCollection(param) &&
                    !!param.vmetaCollectionId
                )
              }
              valueLinkTo={getVmetaCollectionLink(paramsInputs, input.id)}
            />
          )}
        </div>
        {input.inputDetails?.inDescOpt &&
          renderInputDescription(input.inputDetails?.inDescOpt)}
        <Tooltip
          id={`info-${input.id}`}
          className="z-50 max-w-xs whitespace-pre-line break-words"
        />
      </div>
    );
  };

  const renderInputDescription = (info): JSX.Element => (
    <div
      className="mt-1 max-w-[90vw] whitespace-normal text-xs italic text-gray-500"
      dangerouslySetInnerHTML={createMarkup(info)}
    />
  );

  // Build options in showSettings order → then group by onlyModuleName
  const options = (() => {
    const visibleSettings = showSettings.filter(s => !s.startsWith("params."));
    const ordered: ProcessAndModuleOptions[] = [];
    const seen = new Set<string>();

    // 1) Collect items in the exact order of showSettings
    for (const setting of visibleSettings) {
      for (const item of allProcessAndModuleInputs) {
        // Use a unique key for deduplication. Prefer longVarName, fallback to processName, then processOrgName.
        const uniqueKey =
          item.longVarName || item.processName || item.processOrgName;
        if (seen.has(uniqueKey)) continue;
        if (
          item.processName === setting ||
          item.processOrgName === setting ||
          item.longVarName === setting
        ) {
          ordered.push(item);
          seen.add(uniqueKey);
        }
      }
    }

    // 2) Group by module while keeping module order as first encountered
    const grouped = new Map<string, ProcessAndModuleOptions[]>();
    for (const item of ordered) {
      if (!grouped.has(item.onlyModuleName))
        grouped.set(item.onlyModuleName, []);
      grouped.get(item.onlyModuleName)!.push(item);
    }

    // 3) Turn into HorizontalTabs options (preserving group order)
    return Array.from(grouped.entries()).map(([key, value]) => ({
      name: key,
      children: value.map(({ processName, inputs, style }, index) => (
        <div key={`ProcessHorizontalTab-${index}`}>
          <div className="flex items-center justify-between">
            <PageHeading title={processName} size={HeadingSize.XSmall} />
          </div>

          {groupProcessInputs(inputs, style).map(
            ({ inputGroup, isArray, isSpreadsheet }, groupIndex) => (
              <div key={groupIndex}>
                {inputGroup.length === 1 ? (
                  <>
                    {inputGroup[0].inputDetails?.title && (
                      <PageHeading
                        title={inputGroup[0].inputDetails.title}
                        size={HeadingSize.XSmall}
                      />
                    )}
                    {renderInputOptions(inputGroup[0], groupIndex, style)}
                  </>
                ) : (
                  <div key={`multicol-group-${groupIndex}`}>
                    {isSpreadsheet ? (
                      <InputSpreadsheet
                        form={form}
                        inputs={inputGroup}
                        editorProcesses={editorProcesses}
                        paramsInputs={paramsInputs}
                      />
                    ) : (
                      <HorizontalInputsArray
                        form={form}
                        processOptions={processOptions}
                        inputs={inputGroup}
                        style={style}
                        mainScriptHeaderConfig={mainScriptHeaderConfig}
                        renderInputOptions={renderInputOptions}
                        isArray={isArray}
                        readOnly={readOnly}
                      />
                    )}
                  </div>
                )}
              </div>
            )
          )}
        </div>
      )),
    }));
  })();

  return (
    <Modal
      open={showSettings.length > 0}
      setOpen={() => {
        setShowSettings([]);
      }}
      title="Process Settings"
      confirmText="Ok"
      hasCancelButton={false}
      size={ModalSize.Large}
      onConfirm={async () => {
        setShowSettings([]);
      }}>
      {showSettings
        .filter(showSetting => showSetting.startsWith("params."))
        .map((showSetting, index) => {
          const input =
            allGlobalInputs.find(({ id }) => id === showSetting) ||
            allPipelineInputs.find(
              ({ inputId }) => inputId === showSetting.split(".").pop()
            );
          return input ? renderInputOptions(input, index) : null;
        })
        .filter(Boolean)}
      <HorizontalTabs buttonIdPrefix="show-settings" options={options} />
      {!!openInputFileModal && (
        <InputFileModal
          fileOrCollection={openInputFileModal}
          setFileOrCollection={setOpenInputFileModal}
          onSaveFiles={handleSaveInputFiles}
          onSaveValue={handleSaveSingleInputFile}
          paramsInputs={paramsInputs}
          form={form}
        />
      )}
      {!!openInputSingleFileModal && (
        <InputSingleFileModal
          value={openInputSingleFileModal}
          setValue={setOpenInputSingleFileModal}
          onSave={handleSaveSingleInputFile}
          form={form}
          editor={singleFileEditor}
          runUUID={runUUID}
        />
      )}
      {!!openInputValueModal && (
        <InputValueModal
          value={openInputValueModal}
          setValue={setOpenInputValueModal}
          onSave={handleSaveInputValue}
        />
      )}
      {openShowSettingsModal.length > 0 && (
        <ShowSettingsModal
          showSettings={openShowSettingsModal}
          setShowSettings={setOpenShowSettingsModal}
          form={form}
          allGlobalInputs={allGlobalInputs}
          allPipelineInputs={allPipelineInputs}
          allProcessAndModuleInputs={allProcessAndModuleInputs}
          processOptions={processOptions}
          mainScriptHeaderConfig={mainScriptHeaderConfig}
          paramsInputs={paramsInputs}
          setParamsInputs={setParamsInputs}
          readOnly={readOnly}
          editorProcesses={editorProcesses}
          runOwnerId={runOwnerId}
        />
      )}
    </Modal>
  );
};

export default ShowSettingsModal;
